import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
    panNumbersTemplate,
    panNumberFeatureStatusTemplate,
    addPanNumberTemplate,
    updatePanNumberTemplate,
    deletePanNumberTemplate,
    togglePanNumberFeatureTemplate,
} from 'apollo/templates/pan-number';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddUpdatePanNumberDialog from './add-update-pan-number-dialog/add-update-pan-number-dialog.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import { PanNumber } from 'models/pan-number';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

const PanNumberSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [panNumbers, setPanNumbers] = useState<PanNumber[]>([]);
    const [panNumberEnabled, setPanNumberEnabled] = useState(false);
    const [selectedPanNumber, setSelectedPanNumber] = useState<PanNumber>({} as PanNumber);
    const [pendingDeleteId, setPendingDeleteId] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState({
        added: false,
        updated: false,
        deleted: false,
        toggled: false,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(panNumbersTemplate, {
        onCompleted: ({ panNumbers: fetchedPanNumbers }) => {
            setPanNumbers(fetchedPanNumbers);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(panNumberFeatureStatusTemplate, {
        onCompleted: ({ currentLab }) => {
            setPanNumberEnabled(currentLab?.panNumberEnabled ?? false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addPanNumber] = useMutation(addPanNumberTemplate, {
        onCompleted: ({ addPanNumber: updatedList }) => {
            setPanNumbers(updatedList);
            setSnackbarOpen({ added: true, updated: false, deleted: false, toggled: false });
            setAddDialogOpen(false);
            setSelectedPanNumber({} as PanNumber);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updatePanNumber] = useMutation(updatePanNumberTemplate, {
        onCompleted: ({ updatePanNumber: updatedList }) => {
            setPanNumbers(updatedList);
            setSnackbarOpen({ added: false, updated: true, deleted: false, toggled: false });
            setAddDialogOpen(false);
            setSelectedPanNumber({} as PanNumber);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deletePanNumber] = useMutation(deletePanNumberTemplate, {
        onCompleted: ({ deletePanNumber: updatedList }) => {
            setPanNumbers(updatedList);
            setSnackbarOpen({ added: false, updated: false, deleted: true, toggled: false });
            setConfirmDialogOpen(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [togglePanNumberFeature] = useMutation(togglePanNumberFeatureTemplate, {
        onCompleted: ({ togglePanNumberFeature: isEnabled }) => {
            setPanNumberEnabled(isEnabled);
            setSnackbarOpen({ added: false, updated: false, deleted: false, toggled: true });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const resetSnackbars = () => {
        setSnackbarOpen({ added: false, updated: false, deleted: false, toggled: false });
    };

    const onDialogClose = () => {
        setAddDialogOpen(false);
        setConfirmDialogOpen(false);
        setSelectedPanNumber({} as PanNumber);
    };

    const openAddDialog = () => {
        resetSnackbars();
        setAddDialogOpen(true);
    };

    const openEditDialog = (panNumber: PanNumber) => {
        resetSnackbars();
        setSelectedPanNumber(panNumber);
        setAddDialogOpen(true);
    };

    const openConfirmDeleteDialog = (panNumberId: string) => {
        resetSnackbars();
        setPendingDeleteId(panNumberId);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        await deletePanNumber({ variables: { panNumberId: pendingDeleteId } });
        setConfirmDialogOpen(false);
    };

    const handlePanNumberAdd = async (panNumber: PanNumber) => {
        await addPanNumber({ variables: { value: panNumber.value, description: panNumber.description } });
    };

    const handlePanNumberUpdate = async (panNumber: PanNumber) => {
        await updatePanNumber({
            variables: { panNumberId: panNumber._id, value: panNumber.value, description: panNumber.description },
        });
    };

    const handleToggleFeature = async (event: React.ChangeEvent<HTMLInputElement>) => {
        resetSnackbars();
        await togglePanNumberFeature({ variables: { isEnabled: event.target.checked } });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h2" title={translate('lab-settings:PAN_NUMBERS')} />
            <Box mb={2}>{translate('lab-settings:PAN_NUMBERS_DESC')}</Box>
            <Box mb={2}>
                <FormControlLabel
                    control={<Switch checked={panNumberEnabled} onChange={handleToggleFeature} color="primary" />}
                    label={translate('lab-settings:PAN_NUMBER_FEATURE_ENABLED')}
                />
            </Box>
            {panNumberEnabled && (
                <Fragment>
                    <Box mb={2}>
                        <Button onClick={openAddDialog}>{translate('lab-settings:ADD_PAN_NUMBER')}</Button>
                    </Box>
                    {panNumbers.length > 0 ? (
                        <Paper variant="outlined">
                            <List dense>
                                {panNumbers.map((panNumber) => (
                                    <ListItem
                                        key={panNumber._id}
                                        secondaryAction={
                                            <IconButton
                                                onClick={() => openConfirmDeleteDialog(panNumber._id as string)}
                                                edge="end"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemButton onClick={() => openEditDialog(panNumber)}>
                                            <ListItemText primary={panNumber.value} secondary={panNumber.description} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    ) : undefined}
                </Fragment>
            )}
            <AddUpdatePanNumberDialog
                panNumber={selectedPanNumber}
                open={addDialogOpen}
                onDialogClose={onDialogClose}
                onPanNumberAdd={handlePanNumberAdd}
                onPanNumberUpdate={handlePanNumberUpdate}
            />
            <ConfirmationDialog
                open={confirmDialogOpen}
                handleClose={onDialogClose}
                handleOk={handleConfirmDelete}
                title={translate('lab-settings:DELETE_PAN_NUMBER')}
                confirm={translate('lab-settings:CONFIRM_DELETE_PAN_NUMBER')}
                process={translate('common:DELETE')}
            />
            {snackbarOpen.added && (
                <AlertSnackbar type="success" message={translate('lab-settings:PAN_NUMBER_ADDED')} />
            )}
            {snackbarOpen.updated && (
                <AlertSnackbar type="success" message={translate('lab-settings:PAN_NUMBER_UPDATED')} />
            )}
            {snackbarOpen.deleted && (
                <AlertSnackbar type="success" message={translate('lab-settings:PAN_NUMBER_DELETED')} />
            )}
            {snackbarOpen.toggled && (
                <AlertSnackbar
                    type="success"
                    message={translate(
                        panNumberEnabled
                            ? 'lab-settings:PAN_NUMBER_FEATURE_TOGGLED_ON'
                            : 'lab-settings:PAN_NUMBER_FEATURE_TOGGLED_OFF',
                    )}
                />
            )}
        </Fragment>
    );
};

export default PanNumberSettings;
